<template>
    <div class="relative">
        <select
            :id="id"
            :name="name"
            :value="modelValue"
            :required="required"
            :disabled="disabled"
            @change="$emit('update:modelValue', $event.target.value)"
            class="sr-only"
            tabindex="-1"
            aria-hidden="true"
        >
            <option v-if="placeholder && !modelValue" value="" disabled>{{ placeholder }}</option>
            <option
                v-for="option in options"
                :key="option.value"
                :value="option.value"
            >
                {{ option.label }}
            </option>
        </select>

        <!-- Custom select trigger -->
        <button
            type="button"
            @click="toggleDropdown"
            @keydown="handleKeydown"
            :disabled="disabled"
            :class="[
                'w-full h-10 flex items-center justify-between gap-2 px-3 py-2 bg-white border border-gray-400 rounded-md text-left',
                'hover:border-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50',
                disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                isOpen ? 'ring-2 ring-primary ring-opacity-50' : ''
            ]"
            :aria-expanded="isOpen"
            :aria-haspopup="true"
            :aria-labelledby="labelId"
            ref="buttonRef"
        >
            <span class="flex items-center gap-2 flex-1 min-w-0">
                <font-awesome v-if="leftIcon" :icon="leftIcon" class="w-4 h-4 flex-shrink-0" />
                <span :class="!selectedLabel && placeholder ? 'text-gray-700' : 'text-black'" class="whitespace-nowrap">
                    {{ selectedLabel || placeholder || 'Select...' }}
                </span>
            </span>
            <font-awesome 
                :icon="['fas', 'angle-down']" 
                class="w-3 h-3 flex-shrink-0 transition-transform duration-75"
                :class="isOpen ? 'rotate-180' : ''"
            />
        </button>

        <!-- Dropdown menu -->
        <Transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
        >
            <div
                v-if="isOpen"
                @click.stop
                class="absolute z-50 min-w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto focus:outline-none"
                :class="dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full'"
                role="listbox"
                :aria-labelledby="labelId"
                ref="dropdownRef"
            >
                <!-- Search input if searchable -->
                <div v-if="searchable" class="p-2 border-b border-gray-200">
                    <input
                        v-model="searchQuery"
                        @keydown="handleSearchKeydown"
                        type="text"
                        class="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                        placeholder="Search..."
                        ref="searchRef"
                    />
                </div>

                <!-- Options list -->
                <div class="py-1" role="none">
                    <button
                        v-for="(option, index) in filteredOptions"
                        :key="option.value"
                        @click="selectOption(option)"
                        @mouseenter="highlightedIndex = index"
                        type="button"
                        class="w-full flex items-center gap-2 px-3 py-2 text-left transition-colors duration-150"
                        :class="[
                            option.value === modelValue ? 'bg-primary-100 text-primary font-medium' : 'text-gray-900',
                            highlightedIndex === index ? 'bg-gray-100' : '',
                            'hover:bg-gray-100',
                            option.isClearOption ? 'border-b border-gray-200' : ''
                        ]"
                        role="option"
                        :aria-selected="option.value === modelValue"
                    >
                        <font-awesome v-if="option.icon" :icon="option.icon" class="w-4 h-4 flex-shrink-0" />
                        <span class="flex-1 whitespace-nowrap" :class="{'text-gray-600':option.isClearOption}">{{ option.label }}</span>
                        <font-awesome 
                            v-if="option.value === modelValue" 
                            :icon="['fas', 'check']" 
                            class="w-4 h-4 flex-shrink-0 text-primary"
                        />
                    </button>

                    <!-- No results/options message -->
                    <div v-if="filteredOptions.length === 0" class="px-3 py-2 text-sm text-gray-500 text-center">
                        {{ searchable && searchQuery ? 'No results found' : emptyText }}
                    </div>
                </div>
            </div>
        </Transition>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';

const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    options: {
        type: Array,
        required: true,
        validator: (options) => {
            return options.every(opt => 
                typeof opt === 'object' && 
                'value' in opt && 
                'label' in opt
            );
        }
    },
    placeholder: {
        type: String,
        default: ''
    },
    leftIcon: {
        type: [String, Array],
        default: null
    },
    searchable: {
        type: Boolean,
        default: false
    },
    clearable: {
        type: Boolean,
        default: false
    },
    clearableText: {
        type: String,
        default: 'Clear selection'
    },
    emptyText: {
        type: String,
        default: 'No options available'
    },
    disabled: {
        type: Boolean,
        default: false
    },
    required: {
        type: Boolean,
        default: false
    },
    id: {
        type: String,
        default: null
    },
    name: {
        type: String,
        default: null
    },
    labelId: {
        type: String,
        default: null
    }
});

const emit = defineEmits(['update:modelValue', 'change']);

// Refs
const buttonRef = ref(null);
const dropdownRef = ref(null);
const searchRef = ref(null);

// State
const isOpen = ref(false);
const searchQuery = ref('');
const highlightedIndex = ref(-1);
const dropdownPosition = ref('bottom');

// Computed
const selectedOption = computed(() => {
    return props.options.find(opt => opt.value === props.modelValue);
});

const selectedLabel = computed(() => {
    return selectedOption.value?.label || '';
});

const filteredOptions = computed(() => {
    let baseOptions = props.options;
    
    // Add clearable option at the top if enabled and there's a value
    if (props.clearable && props.modelValue) {
        baseOptions = [
            { value: '', label: props.clearableText, isClearOption: true },
            ...props.options
        ];
    }
    
    if (!props.searchable || !searchQuery.value) {
        return baseOptions;
    }
    
    const query = searchQuery.value.toLowerCase();
    return baseOptions.filter(option => 
        option.isClearOption || option.label.toLowerCase().includes(query)
    );
});

// Methods
const toggleDropdown = () => {
    if (props.disabled) return;
    
    if (isOpen.value) {
        closeDropdown();
    } else {
        openDropdown();
    }
};

const openDropdown = async () => {
    isOpen.value = true;
    searchQuery.value = '';
    highlightedIndex.value = filteredOptions.value.findIndex(opt => opt.value === props.modelValue);
    
    await nextTick();
    
    // Check if dropdown should open upwards
    if (typeof window !== 'undefined' && buttonRef.value && dropdownRef.value) {
        const buttonRect = buttonRef.value.getBoundingClientRect();
        const dropdownHeight = dropdownRef.value.offsetHeight;
        const spaceBelow = window.innerHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;
        
        if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
            dropdownPosition.value = 'top';
        } else {
            dropdownPosition.value = 'bottom';
        }
    }
    
    if (props.searchable && searchRef.value) {
        searchRef.value.focus();
    }
    
    // Scroll to selected option when opening (instant, not smooth)
    if (highlightedIndex.value >= 0) {
        scrollToHighlighted(false);
    }
};

const closeDropdown = () => {
    isOpen.value = false;
    highlightedIndex.value = -1;
    
    // Return focus to button
    if (buttonRef.value) {
        buttonRef.value.focus();
    }
};

const selectOption = (option) => {
    emit('update:modelValue', option.value);
    emit('change', option.value);
    closeDropdown();
};

// Scroll to highlighted option
const scrollToHighlighted = async (smooth = true) => {
    await nextTick();
    if (dropdownRef.value && highlightedIndex.value >= 0) {
        const options = dropdownRef.value.querySelectorAll('[role="option"]');
        const highlightedOption = options[highlightedIndex.value];
        if (highlightedOption) {
            highlightedOption.scrollIntoView({ 
                block: 'nearest', 
                behavior: smooth ? 'smooth' : 'instant' 
            });
        }
    }
};

const handleKeydown = (event) => {
    if (!isOpen.value) {
        if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
            event.preventDefault();
            openDropdown();
        }
        return;
    }
    
    switch (event.key) {
        case 'Escape':
            event.preventDefault();
            closeDropdown();
            break;
            
        case 'ArrowDown':
            event.preventDefault();
            if (highlightedIndex.value < filteredOptions.value.length - 1) {
                highlightedIndex.value++;
                scrollToHighlighted();
            }
            break;
            
        case 'ArrowUp':
            event.preventDefault();
            if (highlightedIndex.value > 0) {
                highlightedIndex.value--;
                scrollToHighlighted();
            }
            break;
            
        case 'Enter':
        case ' ':
            event.preventDefault();
            if (highlightedIndex.value >= 0 && highlightedIndex.value < filteredOptions.value.length) {
                selectOption(filteredOptions.value[highlightedIndex.value]);
            }
            break;
            
        case 'Home':
            event.preventDefault();
            highlightedIndex.value = 0;
            scrollToHighlighted();
            break;
            
        case 'End':
            event.preventDefault();
            highlightedIndex.value = filteredOptions.value.length - 1;
            scrollToHighlighted();
            break;
    }
};

const handleSearchKeydown = (event) => {
    switch (event.key) {
        case 'Escape':
            event.preventDefault();
            closeDropdown();
            break;
            
        case 'Enter':
            event.preventDefault();
            if (highlightedIndex.value >= 0 && highlightedIndex.value < filteredOptions.value.length) {
                selectOption(filteredOptions.value[highlightedIndex.value]);
            } else if (filteredOptions.value.length > 0) {
                // If no option is highlighted, select the first one
                selectOption(filteredOptions.value[0]);
            }
            break;
            
        case 'ArrowDown':
            event.preventDefault();
            if (highlightedIndex.value < filteredOptions.value.length - 1) {
                highlightedIndex.value++;
                scrollToHighlighted();
            }
            break;
            
        case 'ArrowUp':
            event.preventDefault();
            if (highlightedIndex.value > 0) {
                highlightedIndex.value--;
                scrollToHighlighted();
            }
            break;
    }
};

// Click outside handler
const handleClickOutside = (event) => {
    if (buttonRef.value && !buttonRef.value.contains(event.target) &&
        dropdownRef.value && !dropdownRef.value.contains(event.target)) {
        closeDropdown();
    }
};

// Watch for changes in filteredOptions to reset highlighted index
watch(filteredOptions, () => {
    highlightedIndex.value = 0;
});

// Lifecycle
onMounted(() => {
    if (typeof document !== 'undefined') {
        document.addEventListener('click', handleClickOutside);
    }
});

onUnmounted(() => {
    if (typeof document !== 'undefined') {
        document.removeEventListener('click', handleClickOutside);
    }
});
</script>